import { useCallback, useEffect, useState } from "react";
import { IEvent, IEventUKCConformationCount, IRunningOrder, IRunningOrderDogClass, IShow, IShowRing } from "@eagerdog/interfaces";
import { getRunningTimeMinutes, isUKCConformationShow, isUKCConformationUnscheduledLevel, syncUKCConformationBISTiles } from "@eagerdog/constants";

import { apiService } from "src/services";
import { groupUKCConformationCounts, syncUKCConformationAssignments } from "./shared/ukcConformationAssignments";

async function saveChangedRings(eventId: string, show: IShow, nextRings: IShowRing[]): Promise<IShow> {
  const rings = show.rings || [];
  try {
    await Promise.all(nextRings
      .filter((ring, index) => JSON.stringify(ring.running_order) !== JSON.stringify(rings[index]?.running_order))
      .map((ring) => apiService.updateRingRunningOrder(eventId, show._id, ring.ring, ring.running_order || [])));
  } catch {
    return show;
  }
  return { ...show, rings: nextRings };
}

/**
 * Judges and classes set on the show (Edit Show > judges) go into the rings. Without this
 * a day the club never opened in the Judge Schedules tab has no judges, and a judge
 * added in the builder gets no classes.
 */
async function withUKCConformationAssignments(event: IEvent, show: IShow): Promise<IShow> {
  if (!isUKCConformationShow(event.sanctioning_club, show.show_type) || (show.rings || []).length === 0) {
    return show;
  }
  let counts: IEventUKCConformationCount[] = [];
  try {
    counts = await apiService.getUKCConformationCounts(event._id, show._id);
  } catch {
    counts = [];
  }
  const perDog = getRunningTimeMinutes(event.sanctioning_club, show.show_type, undefined, undefined) || 0;
  const synced = syncUKCConformationAssignments(show, groupUKCConformationCounts(counts), perDog);
  if (!synced) {
    return show;
  }
  return saveChangedRings(event._id, show, synced);
}

function withoutUnscheduledUKCClasses(sanctioningClub: string, show: IShow): IShow {
  if (!isUKCConformationShow(sanctioningClub, show.show_type)) {
    return show;
  }
  return {
    ...show,
    rings: (show.rings || []).map((ring) => ({
      ...ring,
      running_order: (ring.running_order || []).map((judge) => ({
        ...judge,
        dog_classes: (judge.dog_classes || []).filter((dogClass) => !isUKCConformationUnscheduledLevel(dogClass.level)),
      })),
    })),
  };
}

function showIsEmpty(show: IShow): boolean {
  const rings = show.rings || [];
  return rings.length === 0 || rings.every((ring) => !ring.running_order || ring.running_order.length === 0);
}

function sameTile(saved: IRunningOrderDogClass, api: IRunningOrderDogClass): boolean {
  return (saved.show_element || "") === (api.show_element || "") &&
    (saved.level || "") === (api.level || "") &&
    (saved.section || "") === (api.section || "");
}

function applySummary(show: IShow, ringName: string, summary: IRunningOrder[]): IShow {
  return {
    ...show,
    rings: (show.rings || []).map((ring) => {
      if (ring.ring !== ringName) {
        return ring;
      }
      return {
        ...ring,
        running_order: (ring.running_order || []).map((judge) => {
          const apiJudge = summary.find((item) => item.judge_name === judge.judge_name && (item.judge_number || "") === (judge.judge_number || ""))
            || summary.find((item) => item.judge_name === judge.judge_name);
          if (!apiJudge) {
            return judge;
          }
          return {
            ...judge,
            dog_classes: (judge.dog_classes || []).map((dogClass) => {
              if (dogClass.type === "break" || dogClass.split_number != null) {
                return dogClass;
              }
              const match = (apiJudge.dog_classes || []).find((apiClass) => sameTile(dogClass, apiClass));
              if (!match) {
                return dogClass;
              }
              const entries = match.entries ?? dogClass.entries ?? 0;
              const perEntry = (dogClass.duration && dogClass.duration > 0 ? dogClass.duration : match.duration) || 0;
              const floor = perEntry * entries;
              const saved = dogClass.total_minutes;
              const apiTotal = match.total_minutes ?? floor;
              return {
                ...dogClass,
                entries,
                duration: dogClass.duration || match.duration,
                total_minutes: saved != null && saved > floor + 0.05 ? saved : apiTotal,
              };
            }),
          };
        }),
      };
    }),
  };
}

async function withUKCConformationBISTiles(eventId: string, sanctioningClub: string, show: IShow): Promise<IShow> {
  if (!isUKCConformationShow(sanctioningClub, show.show_type)) {
    return show;
  }
  const synced = syncUKCConformationBISTiles(show.show_elements, show.rings || [], show.hidden_bis_classes || []);
  if (!synced) {
    return show;
  }
  return saveChangedRings(eventId, show, synced);
}

async function enrichShow(eventId: string, show: IShow): Promise<IShow> {
  let next = show;
  const rings = show.rings || [];
  const summaries = await Promise.all(rings.map(async (ring) => {
    try {
      const summary = await apiService.getShowRunningOrderSummary(eventId, show._id, ring.ring);
      return { ring: ring.ring, summary };
    } catch {
      return { ring: ring.ring, summary: null as IRunningOrder[] | null };
    }
  }));
  summaries.forEach((item) => {
    if (item.summary) {
      next = applySummary(next, item.ring, item.summary);
    }
  });
  return next;
}

export function useJudgeScheduleBuilderData(event: IEvent, anchorShowId: string) {
  const [shows, setShows] = useState<IShow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const loaded = await apiService.getClubEventShows(event.club_id, event._id);
      const prepared: IShow[] = [];
      // Every empty show gets seeded, so a day that was never opened in the Judge
      // Schedules tab still shows its judges here.
      for (const show of loaded) {
        if (showIsEmpty(show)) {
          try {
            prepared.push(await apiService.syncShowRingsWithScores(event._id, show._id));
            continue;
          } catch {
            prepared.push(show);
            continue;
          }
        }
        prepared.push(show);
      }
      const enriched = await Promise.all(
        prepared.map(async (show) => enrichShow(
          event._id,
          await withUKCConformationBISTiles(
            event._id,
            event.sanctioning_club,
            await withUKCConformationAssignments(event, withoutUnscheduledUKCClasses(event.sanctioning_club, show))
          )
        ))
      );
      setShows(enriched);
    } catch {
      setLoadError("Couldn't load the judge schedule. Close and try again.");
    } finally {
      setLoading(false);
    }
  }, [event.club_id, event._id, event.sanctioning_club, anchorShowId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { shows, setShows, loading, loadError, reload };
}
